/**
 * AgentInstructionsModal
 *
 * Modal for viewing the agent's full prompt (read-only sections)
 * and editing custom instructions (user-editable section).
 *
 * Features:
 * - Collapsible read-only prompt sections (identity, capabilities, etc.)
 * - Editable custom instructions with 3000 char limit
 * - Business-type templates (one-click fill)
 * - Instruction blocks (add/remove reusable sections)
 * - AI assistant (uses existing copilot endpoint)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Spinner, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  IconFileText,
  IconChevronDown,
  IconChevronRight,
  IconLock,
  IconPlus,
  IconTrash,
  IconSparkles,
  IconSend,
  IconTemplate,
  IconPuzzle,
  IconX,
  IconCheck,
  IconAlertCircle,
  IconEdit,
  IconExternalLink,
  IconInfoCircle,
} from '@tabler/icons-react';
import AgentsFlowService from '../services/AgentsFlowService';
import { INSTRUCTION_TEMPLATES, INSTRUCTION_BLOCKS } from '../constants/instructionTemplates';
import '../styles/agent-instructions-modal.css';

const MAX_CHARS = 3000;

// ─── Prompt Section (collapsible, read-only) ─────────────────────────────────

const PromptSection = ({ title, source, content, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  if (!content) return null;

  return (
    <div className="aim-prompt-section">
      <button
        className="aim-prompt-section__header"
        onClick={() => setOpen(!open)}
      >
        <span className="aim-prompt-section__toggle">
          {open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
        </span>
        <span className="aim-prompt-section__title">{title}</span>
        <span className="aim-prompt-section__source">
          <IconLock size={12} /> {source}
        </span>
      </button>
      {open && (
        <pre className="aim-prompt-section__content">{content}</pre>
      )}
    </div>
  );
};

// ─── Template Selector ───────────────────────────────────────────────────────

const TemplateSelector = ({ onSelect, onClose }) => (
  <div className="aim-templates">
    <div className="aim-templates__header">
      <h6>Escolha um modelo para começar</h6>
      <button className="aim-templates__close" onClick={onClose}>
        <IconX size={16} />
      </button>
    </div>
    <div className="aim-templates__grid">
      {Object.entries(INSTRUCTION_TEMPLATES).map(([key, tmpl]) => (
        <button
          key={key}
          className="aim-template-card"
          onClick={() => onSelect(tmpl.text)}
        >
          <span className="aim-template-card__icon">{tmpl.icon}</span>
          <span className="aim-template-card__label">{tmpl.label}</span>
        </button>
      ))}
    </div>
    <p className="aim-templates__hint">
      O modelo será adicionado ao editor. Substitua os textos entre [colchetes] pelas suas informações.
    </p>
  </div>
);

// ─── Block Selector ──────────────────────────────────────────────────────────

const BlockSelector = ({ onAdd, onClose, addedBlocks }) => (
  <div className="aim-blocks">
    <div className="aim-blocks__header">
      <h6>Adicionar bloco de instrução</h6>
      <button className="aim-blocks__close" onClick={onClose}>
        <IconX size={16} />
      </button>
    </div>
    <div className="aim-blocks__list">
      {INSTRUCTION_BLOCKS.map((block) => {
        const isAdded = addedBlocks.includes(block.id);
        return (
          <button
            key={block.id}
            className={`aim-block-item ${isAdded ? 'aim-block-item--added' : ''}`}
            onClick={() => !isAdded && onAdd(block)}
            disabled={isAdded}
          >
            <span className="aim-block-item__icon">{block.icon}</span>
            <span className="aim-block-item__label">{block.label}</span>
            {isAdded ? (
              <IconCheck size={16} className="aim-block-item__status" />
            ) : (
              <IconPlus size={16} className="aim-block-item__action" />
            )}
          </button>
        );
      })}
    </div>
  </div>
);

// ─── AI Assistant ────────────────────────────────────────────────────────────

const AIAssistant = ({ agentId, onApply, onClose, currentInstructions }) => {
  const [message, setMessage] = useState('');
  const [conversation, setConversation] = useState([
    {
      role: 'assistant',
      content: 'Me conte sobre o que seu agente precisa saber. Descreva regras, políticas ou informações do seu negócio e eu vou transformar em instruções claras para o agente.',
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [generatedInstructions, setGeneratedInstructions] = useState('');
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const handleSend = async () => {
    if (!message.trim() || loading) return;

    const userMessage = message.trim();
    setMessage('');
    setConversation((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const context = {
        task: 'generate_instructions',
        current_instructions: currentInstructions || '',
        max_chars: MAX_CHARS,
      };

      const prompt = `O usuário quer criar instruções personalizadas para o agente de IA dele.
Com base na mensagem abaixo, gere instruções claras e bem organizadas em formato de tópicos com seções (usando ## para títulos).
As instruções devem ser diretas, usar linguagem simples e ter no máximo ${MAX_CHARS} caracteres.
NÃO use markdown complexo, apenas ## para títulos e - para listas.
${currentInstructions ? `\nInstruções atuais do agente:\n${currentInstructions}\n` : ''}
Mensagem do usuário: ${userMessage}

Responda APENAS com as instruções geradas, sem explicações extras.`;

      const response = await AgentsFlowService.copilot(agentId, {
        message: prompt,
        context,
      });

      const reply = response.data?.response || response.data?.message || '';

      setConversation((prev) => [
        ...prev,
        { role: 'assistant', content: 'Pronto! Gerei as instruções com base no que você me disse. Veja se está tudo certo:' },
      ]);
      setGeneratedInstructions(reply);
    } catch (err) {
      setConversation((prev) => [
        ...prev,
        { role: 'assistant', content: 'Desculpe, não consegui gerar as instruções. Tente novamente.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="aim-assistant">
      <div className="aim-assistant__header">
        <div className="aim-assistant__title">
          <IconSparkles size={18} />
          <h6>Assistente de Instruções</h6>
        </div>
        <button className="aim-assistant__close" onClick={onClose}>
          <IconX size={16} />
        </button>
      </div>

      <div className="aim-assistant__chat">
        {conversation.map((msg, i) => (
          <div key={i} className={`aim-chat-msg aim-chat-msg--${msg.role}`}>
            {msg.content}
          </div>
        ))}
        {loading && (
          <div className="aim-chat-msg aim-chat-msg--assistant">
            <Spinner animation="border" size="sm" /> Gerando instruções...
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {generatedInstructions && (
        <div className="aim-assistant__preview">
          <div className="aim-assistant__preview-header">
            <span>Instruções geradas:</span>
            <span className="aim-assistant__preview-chars">
              {generatedInstructions.length}/{MAX_CHARS}
            </span>
          </div>
          <pre className="aim-assistant__preview-content">{generatedInstructions}</pre>
          <div className="aim-assistant__preview-actions">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setGeneratedInstructions('')}
            >
              Descartar
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                onApply(generatedInstructions.slice(0, MAX_CHARS));
                onClose();
              }}
            >
              <IconCheck size={16} /> Usar estas instruções
            </Button>
          </div>
        </div>
      )}

      <div className="aim-assistant__input">
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder="Ex: Vendo carros usados, aceito financiamento CDC e consórcio..."
          disabled={loading}
        />
        <button
          className="aim-assistant__send"
          onClick={handleSend}
          disabled={!message.trim() || loading}
        >
          <IconSend size={18} />
        </button>
      </div>
    </div>
  );
};

// ─── Main Modal ──────────────────────────────────────────────────────────────

const AgentInstructionsModal = ({
  show,
  onHide,
  agentId,
  agentData,
  onSave,
}) => {
  const [instructions, setInstructions] = useState('');
  const [activePanel, setActivePanel] = useState(null); // 'templates' | 'blocks' | 'assistant'
  const [addedBlocks, setAddedBlocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Load existing instructions from custom_messages.system_prompt
  useEffect(() => {
    if (show && agentData) {
      const existing = agentData.custom_messages?.system_prompt || '';
      setInstructions(existing);
      setDirty(false);
      setActivePanel(null);
      setAddedBlocks([]);
    }
  }, [show, agentData]);

  const handleInstructionsChange = useCallback((value) => {
    if (value.length <= MAX_CHARS) {
      setInstructions(value);
      setDirty(true);
    }
  }, []);

  const handleTemplateSelect = useCallback((text) => {
    const newText = text.slice(0, MAX_CHARS);
    setInstructions(newText);
    setDirty(true);
    setActivePanel(null);
  }, []);

  const handleBlockAdd = useCallback((block) => {
    setInstructions((prev) => {
      const separator = prev.trim() ? '\n\n' : '';
      const newText = (prev + separator + block.text).slice(0, MAX_CHARS);
      return newText;
    });
    setAddedBlocks((prev) => [...prev, block.id]);
    setDirty(true);
  }, []);

  const handleAIApply = useCallback((text) => {
    setInstructions(text);
    setDirty(true);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedCustomMessages = {
        ...(agentData.custom_messages || {}),
        system_prompt: instructions,
      };

      await AgentsFlowService.updateAgent(agentId, {
        custom_messages: updatedCustomMessages,
      });

      if (onSave) {
        onSave(updatedCustomMessages);
      }

      setDirty(false);
      onHide();
    } catch (err) {
      console.error('Erro ao salvar instruções:', err);
    } finally {
      setSaving(false);
    }
  };

  // Build read-only prompt sections from agent data
  const identityContent = buildIdentitySection(agentData);
  const businessContent = buildBusinessSection(agentData);
  const behaviorContent = buildBehaviorSection(agentData);

  const charsUsed = instructions.length;
  const charsPercent = (charsUsed / MAX_CHARS) * 100;
  const isNearLimit = charsPercent > 85;
  const isAtLimit = charsUsed >= MAX_CHARS;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="aim-modal"
      backdrop="static"
    >
      <Modal.Header className="aim-header">
        <div className="aim-header__title">
          <IconFileText size={22} />
          <div>
            <h5>Instruções do Agente</h5>
            <span className="aim-header__subtitle">
              Defina regras e informações para seu agente seguir
            </span>
          </div>
        </div>
        <button className="aim-header__close" onClick={onHide}>
          <IconX size={20} />
        </button>
      </Modal.Header>

      <Modal.Body className="aim-body">
        <div className="aim-layout">

          {/* ── Left Panel: Read-only prompt ── */}
          <div className="aim-left">
            <div className="aim-left__header">
              <h6>Prompt atual do agente</h6>
              <span className="aim-left__badge">Somente leitura</span>
            </div>
            <p className="aim-left__desc">
              Estas seções são geradas automaticamente pelas configurações do agente.
              Para editá-las, use as <strong>Configurações Avançadas</strong>.
            </p>

            <div className="aim-prompt-sections">
              <PromptSection
                title="Identidade"
                source="via Identidade"
                content={identityContent}
              />
              <PromptSection
                title="Dados do negócio"
                source="via Configurações"
                content={businessContent}
              />
              <PromptSection
                title="Regras de comportamento"
                source="via Configurações"
                content={behaviorContent}
              />
            </div>

            <div className="aim-left__auto-info">
              <IconInfoCircle size={14} />
              <span>
                <strong>Horário de funcionamento</strong> e <strong>Tipos de atendimento</strong> são adicionados automaticamente ao prompt quando cadastrados em <strong>Minha Empresa</strong>.
                Não é necessário repeti-los nas instruções.
              </span>
            </div>

            <div className="aim-left__tip">
              <IconExternalLink size={14} />
              <span>
                Para editar estas seções, feche este modal e abra <strong>Configurações Avançadas</strong> na barra superior.
              </span>
            </div>
          </div>

          {/* ── Right Panel: Editable instructions ── */}
          <div className="aim-right">
            <div className="aim-right__header">
              <h6>
                <IconEdit size={18} />
                Suas instruções personalizadas
              </h6>
            </div>

            {/* Action buttons */}
            <div className="aim-actions">
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Começar com um modelo pronto do seu nicho</Tooltip>}
              >
                <button
                  className={`aim-action-btn ${activePanel === 'templates' ? 'aim-action-btn--active' : ''}`}
                  onClick={() => setActivePanel(activePanel === 'templates' ? null : 'templates')}
                >
                  <IconTemplate size={16} />
                  Modelos
                </button>
              </OverlayTrigger>

              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Adicionar blocos de instrução prontos</Tooltip>}
              >
                <button
                  className={`aim-action-btn ${activePanel === 'blocks' ? 'aim-action-btn--active' : ''}`}
                  onClick={() => setActivePanel(activePanel === 'blocks' ? null : 'blocks')}
                >
                  <IconPuzzle size={16} />
                  Blocos
                </button>
              </OverlayTrigger>

              {agentId && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Descreva suas regras e a IA gera as instruções</Tooltip>}
                >
                  <button
                    className={`aim-action-btn aim-action-btn--ai ${activePanel === 'assistant' ? 'aim-action-btn--active' : ''}`}
                    onClick={() => setActivePanel(activePanel === 'assistant' ? null : 'assistant')}
                  >
                    <IconSparkles size={16} />
                    Gerar com IA
                  </button>
                </OverlayTrigger>
              )}

              {instructions.trim() && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Limpar todas as instruções</Tooltip>}
                >
                  <button
                    className="aim-action-btn aim-action-btn--danger"
                    onClick={() => {
                      setInstructions('');
                      setAddedBlocks([]);
                      setDirty(true);
                    }}
                  >
                    <IconTrash size={16} />
                  </button>
                </OverlayTrigger>
              )}
            </div>

            {/* Active panel (templates / blocks / assistant) */}
            {activePanel === 'templates' && (
              <TemplateSelector
                onSelect={handleTemplateSelect}
                onClose={() => setActivePanel(null)}
              />
            )}
            {activePanel === 'blocks' && (
              <BlockSelector
                onAdd={handleBlockAdd}
                onClose={() => setActivePanel(null)}
                addedBlocks={addedBlocks}
              />
            )}
            {activePanel === 'assistant' && agentId && (
              <AIAssistant
                agentId={agentId}
                onApply={handleAIApply}
                onClose={() => setActivePanel(null)}
                currentInstructions={instructions}
              />
            )}

            {/* Textarea */}
            <div className="aim-editor">
              {!instructions.trim() && !activePanel && (
                <div className="aim-editor__empty">
                  <p>Nenhuma instrução personalizada ainda.</p>
                  <p>Use os botões acima para começar com um <strong>modelo pronto</strong>, adicionar <strong>blocos</strong> ou <strong>gerar com IA</strong>.</p>
                </div>
              )}
              <textarea
                className="aim-editor__textarea"
                value={instructions}
                onChange={(e) => handleInstructionsChange(e.target.value)}
                placeholder="Escreva aqui as regras e informações que seu agente deve seguir...&#10;&#10;Exemplo:&#10;## Financiamento&#10;- Aceitamos CDC e consórcio&#10;- Entrada mínima: 30%&#10;&#10;## Negociação&#10;- Desconto máximo sem aprovação: 5%"
                rows={12}
              />
              <div className={`aim-editor__counter ${isNearLimit ? 'aim-editor__counter--warning' : ''} ${isAtLimit ? 'aim-editor__counter--danger' : ''}`}>
                {isAtLimit && <IconAlertCircle size={14} />}
                {charsUsed.toLocaleString()}/{MAX_CHARS.toLocaleString()} caracteres
              </div>
            </div>

            {/* Helper text */}
            <Alert variant="light" className="aim-help-alert">
              <strong>Dica:</strong> Use <code>## Título</code> para criar seções e <code>- item</code> para listas.
              Substitua os textos entre <code>[colchetes]</code> pelas suas informações reais.
            </Alert>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="aim-footer">
        <Button variant="outline-secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || !dirty}
          className="aim-save-btn"
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" /> Salvando...
            </>
          ) : (
            <>
              <IconCheck size={18} /> Salvar instruções
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── Helper Functions ────────────────────────────────────────────────────────

function buildIdentitySection(data) {
  if (!data) return '';
  const ic = data.identity_config || {};
  const parts = [];

  if (ic.display_name) parts.push(`Nome: ${ic.display_name}`);
  if (ic.role) parts.push(`Função: ${ic.role}`);
  if (ic.tone) parts.push(`Tom: ${ic.tone}`);
  if (ic.gender) parts.push(`Gênero: ${ic.gender}`);
  if (ic.personality_traits?.length) {
    parts.push(`Personalidade: ${ic.personality_traits.join(', ')}`);
  }

  return parts.length ? parts.join('\n') : '';
}

function buildBusinessSection(data) {
  if (!data) return '';
  const bi = data.business_info || {};
  const parts = [];

  if (bi.business_name) parts.push(`Empresa: ${bi.business_name}`);
  if (bi.address) parts.push(`Endereço: ${bi.address}`);
  if (bi.city) parts.push(`Cidade: ${bi.city}${bi.state ? ` - ${bi.state}` : ''}`);
  if (bi.phone) parts.push(`Telefone: ${bi.phone}`);
  if (bi.email) parts.push(`E-mail: ${bi.email}`);
  if (bi.website) parts.push(`Site: ${bi.website}`);
  if (bi.payment_methods?.length) {
    parts.push(`Pagamento: ${bi.payment_methods.join(', ')}`);
  }

  return parts.length ? parts.join('\n') : '';
}

function buildBehaviorSection(data) {
  if (!data) return '';
  const bc = data.behavior_config || {};
  const parts = [];

  if (bc.rules?.length) {
    parts.push('Regras:');
    bc.rules.forEach((r) => parts.push(`  - ${r}`));
  }
  if (bc.transfer_conditions?.length) {
    parts.push(`Transferir quando: ${bc.transfer_conditions.join(', ')}`);
  }
  if (bc.max_attempts_before_transfer) {
    parts.push(`Máx. tentativas antes de transferir: ${bc.max_attempts_before_transfer}`);
  }

  return parts.length ? parts.join('\n') : '';
}

export default AgentInstructionsModal;
